import React, { useEffect, useState } from "react";
import { Grid, LinearProgress, Paper, Skeleton, Box, TextField, Button, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { usePublicationsController } from "./context/publications-controller";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";
import { usePublicationsState } from "./context/publications-context";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { IListPublicationsRequest } from "../../model/base/request/list-publications-request";
import { IPublicationResponse } from "../../model/base/response/publication-response";
import PageHeader from "../components/shared/page-header";
import ClearIcon from '@mui/icons-material/Clear';

export function PublicationsComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData, loadCategories, loadSubcategories, loadPublishingVehicles } = usePublicationsController();
    const navigate = useNavigate();
    const { categoryId, subcategoryId } = useParams<{ categoryId?: string; subcategoryId?: string }>();
    const { publications, categories, subcategories, publishingVehicles } = usePublicationsState();
    const [paramsRequest, setParamsRequest] = useState<IListPublicationsRequest>({ page: 0, pageSize: 10 } as IListPublicationsRequest);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
    const [selectedSubcategory, setSelectedSubcategory] = useState<number | ''>('');
    const [selectedPublishingVehicle, setSelectedPublishingVehicle] = useState<number | ''>('');
    const [isFilterLockedByUrl, setIsFilterLockedByUrl] = useState(false);
    const [dataGridKey, setDataGridKey] = useState(0);

    // Inicializa os filtros a partir dos parâmetros da URL
    useEffect(() => {
        const initializeFromUrl = async () => {
            if (categoryId) {
                const catId = parseInt(categoryId);
                setSelectedCategory(catId);
                setIsFilterLockedByUrl(true); // Bloqueia os filtros quando vêm da URL

                // Carrega subcategorias antes de definir a subcategoria selecionada
                await loadSubcategories(catId);

                if (subcategoryId) {
                    const subCatId = parseInt(subcategoryId);
                    setSelectedSubcategory(subCatId);
                }
            }
        };
        initializeFromUrl();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId, subcategoryId]);

    useEffect(() => {
        loadCategories();
        loadPublishingVehicles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Só carrega subcategorias quando categoria é alterada manualmente (não está bloqueada pela URL)
        if (selectedCategory && !isFilterLockedByUrl) {
            loadSubcategories(selectedCategory as number);
            setSelectedSubcategory('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, isFilterLockedByUrl]);

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequest);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramsRequest]);

    useEffect(() => {
        setParamsRequest(prev => ({
            ...prev,
            startDate: startDate?.format('YYYY-MM-DD'),
            endDate: endDate?.format('YYYY-MM-DD'),
            categoryId: selectedCategory || undefined,
            subcategoryId: selectedSubcategory || undefined,
            publishingVehicleId: selectedPublishingVehicle || undefined,
            page: 0,
        }));
        // Atualiza a key do DataGrid para forçar re-renderização
        setDataGridKey(prev => prev + 1);
    }, [startDate, endDate, selectedCategory, selectedSubcategory, selectedPublishingVehicle]);

    const handleClearFilters = () => {
        // Libera os filtros ao limpar
        setIsFilterLockedByUrl(false);

        // Remove os parâmetros da URL navegando para a rota sem filtros
        navigate('/publicacoes', { replace: true });

        // Limpa todos os filtros
        setParamsRequest({ page: 0, pageSize: 10 } as IListPublicationsRequest);
        setStartDate(null);
        setEndDate(null);
        setSelectedCategory('');
        setSelectedSubcategory('');
        setSelectedPublishingVehicle('');

        // Atualiza a key do DataGrid para forçar re-renderização
        setDataGridKey(prev => prev + 1);
    };

    const columns: GridColDef<IPublicationResponse>[] = [
        {
            field: 'editionNumber',
            headerName: 'N Edição',
            minWidth: 100,
            flex: 0.8,
            renderCell: (params) => {
                return params.row.editionNumber ? `${params.row.editionNumber}/${params.row.year}` : '-';
            }
        },
        {
            field: 'description',
            headerName: 'Descrição',
            minWidth: 200,
            flex: 3,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            overflow: 'visible',
                            textOverflow: 'clip',
                            display: 'block',
                            width: '100%'
                        }}
                    >
                        {params.row.description}
                    </Box>
                );
            }
        },
        {
            field: 'category',
            headerName: 'Categoria',
            minWidth: 120,
            flex: 1.2,
        },
        {
            field: 'subCategory',
            headerName: 'Subcategoria',
            minWidth: 120,
            flex: 1.2,
            renderCell: (params) => {
                return params.row.subCategory || '-';
            }
        },
        {
            field: 'publishingVehicle',
            headerName: 'Veículo de Publicação',
            minWidth: 150,
            flex: 1.3,
            renderCell: (params) => {
                return params.row.publishingVehicle || '-';
            }
        },
        {
            field: 'publicationDate',
            headerName: 'Data de Publicação',
            minWidth: 130,
            flex: 1,
            valueFormatter: params => params?.value ? dayjs(params.value).format('DD/MM/YYYY') : '-',
        },
        {
            field: 'actions',
            headerName: 'Ações',
            minWidth: 150,
            flex: 1.5,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        {params.row.fileViewUrl && (
                            <Button
                                variant="text"
                                size="small"
                                endIcon={<DescriptionIcon />}
                                href={params.row.fileViewUrl}
                                target="_blank"
                                rel="noopener"
                                component="a"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    minWidth: '130px',
                                    padding: '4px 12px'
                                }}
                            >
                                Visualizar
                            </Button>
                        )}
                        {params.row.fileDownloadUrl && (
                            <Button
                                variant="text"
                                size="small"
                                endIcon={<DescriptionIcon />}
                                href={params.row.fileDownloadUrl}
                                target="_blank"
                                rel="noopener"
                                component="a"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    minWidth: '130px',
                                    padding: '4px 12px'
                                }}
                            >
                                Baixar
                            </Button>
                        )}
                        <Button
                            variant="text"
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate(`/publicacoes/detalhes/${params.row.id}`)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                minWidth: '130px',
                                padding: '4px 12px'
                            }}
                        >
                            Ver Detalhes
                        </Button>
                    </Box>
                );
            }
        },
    ];

    return (
        <Box>
            <PageHeader
                title="Publicações do Diário Oficial"
                subtitle="Consulte as publicações do diário oficial"
                breadcrumbs={[{ label: 'Publicações do Diário Oficial' }]}
            />

            {initialLoading ? (
                <Box>
                    <Skeleton variant="rounded" width="100%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="rounded" width="100%" height={400} />
                </Box>
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Buscar por descrição"
                                    placeholder="Digite para buscar na descrição"
                                    variant="outlined"
                                    value={paramsRequest.title || ''}
                                    onChange={(e) => setParamsRequest({ ...paramsRequest, title: e.target.value, page: 0 })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Data Início"
                                        value={startDate}
                                        onChange={(newValue) => setStartDate(newValue)}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                variant: "outlined"
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Data Fim"
                                        value={endDate}
                                        onChange={(newValue) => setEndDate(newValue)}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                variant: "outlined"
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth disabled={isFilterLockedByUrl}>
                                    <InputLabel>Categoria</InputLabel>
                                    <Select
                                        value={selectedCategory}
                                        label="Categoria"
                                        onChange={(e) => setSelectedCategory(e.target.value as number | '')}
                                    >
                                        <MenuItem value="">
                                            <em>Todas</em>
                                        </MenuItem>
                                        {categories?.map((category) => (
                                            <MenuItem key={category.id} value={category.id}>
                                                {category.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth disabled={isFilterLockedByUrl || !selectedCategory}>
                                    <InputLabel>Subcategoria</InputLabel>
                                    <Select
                                        value={selectedSubcategory}
                                        label="Subcategoria"
                                        onChange={(e) => setSelectedSubcategory(e.target.value as number | '')}
                                    >
                                        <MenuItem value="">
                                            <em>Todas</em>
                                        </MenuItem>
                                        {subcategories?.map((subcategory) => (
                                            <MenuItem key={subcategory.id} value={subcategory.id}>
                                                {subcategory.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Veículo de Publicação</InputLabel>
                                    <Select
                                        value={selectedPublishingVehicle}
                                        label="Veículo de Publicação"
                                        onChange={(e) => setSelectedPublishingVehicle(e.target.value as number | '')}
                                    >
                                        <MenuItem value="">
                                            <em>Todos</em>
                                        </MenuItem>
                                        {publishingVehicles?.map((vehicle) => (
                                            <MenuItem key={vehicle.id} value={vehicle.id}>
                                                {vehicle.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    sx={{ height: '56px', minWidth: '150px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, minHeight: 500 }} elevation={2}>
                        <DataGrid
                            key={`datagrid-${dataGridKey}`}
                            rows={publications?.items || []}
                            rowCount={publications?.totalRecords || 0}
                            columns={columns}
                            getRowId={(row) => `${row.id}-${row.publishingVehicle || 'no-vehicle'}`}
                            hideFooterSelectedRowCount
                            paginationModel={{ page: paramsRequest.page || 0, pageSize: paramsRequest.pageSize || 10 } as GridPaginationModel}
                            onPaginationModelChange={(e) => { setParamsRequest({ ...paramsRequest, page: e.page, pageSize: e.pageSize }) }}
                            pageSizeOptions={[10]}
                            paginationMode="server"
                            getRowHeight={() => 'auto'}
                            disableColumnMenu
                            slots={{
                                loadingOverlay: LinearProgress,
                            }}
                            loading={loadMoreData}
                            localeText={{
                                MuiTablePagination: {
                                    labelDisplayedRows: ({ from, to, count }) =>
                                        `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`,
                                    labelRowsPerPage: 'Linhas por página:',
                                },
                                noRowsLabel: 'Nenhum registro encontrado',
                                noResultsOverlayLabel: 'Nenhum resultado encontrado',
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiDataGrid-main': {
                                    overflow: 'hidden',
                                },
                                '& .MuiDataGrid-virtualScroller': {
                                    overflowX: 'hidden !important',
                                },
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    lineHeight: 'normal !important',
                                    maxHeight: 'none !important',
                                    minHeight: '52px !important',
                                    whiteSpace: 'normal',
                                    py: 1.5,
                                },
                                '& .MuiDataGrid-row': {
                                    maxHeight: 'none !important',
                                },
                            }}
                        />
                    </Paper>
                </React.Fragment>
            )}
        </Box>
    );
}
